class CartDrawer {
  constructor() {
    this.drawer = document.getElementById("cart-drawer");
    this.overlay = this.drawer?.querySelector(".cart-drawer__overlay");
    this.closeBtn = this.drawer?.querySelector(".cart-drawer__close");
    this.isOpen = false;
    this.isUpdatingFromDrawer = false;
    this.isUpdatingFromTrigger = false;

    this.init();
  }

  init() {
    if (!this.drawer) return;

    // Close button
    this.closeBtn?.addEventListener("click", () => this.close());

    // Overlay click
    this.overlay?.addEventListener("click", () => this.close());

    // ESC key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isOpen) {
        this.close();
      }
    });

    // Quantity controls
    this.drawer.addEventListener("click", (e) => {
      const target = e.target.closest("[data-action]");
      if (!target) return;

      const action = target.dataset.action;
      const itemKey = target.dataset.cartItemKey;

      if (action === "increase") {
        this.updateQuantity(itemKey, 1);
      } else if (action === "decrease") {
        this.updateQuantity(itemKey, -1);
      } else if (action === "remove") {
        this.removeItem(itemKey);
      }
    });

    // Quantity input changes
    this.drawer.addEventListener("change", (e) => {
      if (e.target.classList.contains("quantity-value")) {
        const itemKey = e.target.dataset.cartItemKey;
        const quantity = parseInt(e.target.value);
        this.updateQuantity(itemKey, quantity, true);
      }
    });
  }

  async open() {
    if (this.isOpen) return;

    this.drawer.classList.add("is-open");
    document.body.classList.add("cart-drawer-open");
    this.isOpen = true;

    // Focus management
    this.closeBtn?.focus();
  }

  async openWithUpdatedContent() {
    if (this.isOpen) return;

    // Update cart content and count before opening
    try {
      const response = await fetch("/cart");
      if (response.ok) {
        const html = await response.text();
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = html;

        // Update cart drawer content
        const fetchedCartDrawerContent = tempDiv.querySelector(".cart-drawer__content");
        if (fetchedCartDrawerContent) {
          const currentCartDrawerContent = this.drawer.querySelector(".cart-drawer__content");
          if (currentCartDrawerContent) {
            currentCartDrawerContent.innerHTML = fetchedCartDrawerContent.innerHTML;
          }
        }

        // Update cart count in header
        const fetchedCartCountElements = tempDiv.querySelectorAll("[data-cart-count]");
        const currentCartCountElements = document.querySelectorAll("[data-cart-count]");

        if (fetchedCartCountElements.length > 0 && currentCartCountElements.length > 0) {
          const newCartCount = fetchedCartCountElements[0].textContent;
          currentCartCountElements.forEach((el) => {
            el.textContent = newCartCount;
            el.style.display = parseInt(newCartCount) > 0 ? "inline" : "none";
          });
        }

        // Update cart button visibility
        const cartButtons = document.querySelectorAll(".cart-drawer-trigger");
        const cartCount = parseInt(fetchedCartCountElements[0]?.textContent || "0");
        cartButtons.forEach((button) => {
          if (cartCount === 0) {
            button.style.display = "none";
          } else {
            button.style.display = "inline-flex";
          }
        });
      }
    } catch (error) {
      console.error("Error updating cart content before opening:", error);
    }

    // Now open the drawer
    this.drawer.classList.add("is-open");
    document.body.classList.add("cart-drawer-open");
    this.isOpen = true;

    // Focus management
    this.closeBtn?.focus();
  }

  close() {
    if (!this.isOpen) return;

    this.drawer.classList.remove("is-open");
    document.body.classList.remove("cart-drawer-open");
    this.isOpen = false;
  }

  async updateQuantity(itemKey, change, isDirectInput = false) {
    const input = this.drawer.querySelector(`[data-cart-item-key="${itemKey}"] .quantity-value`);
    if (!input) return;

    let newQuantity;
    if (isDirectInput) {
      newQuantity = change;
    } else {
      newQuantity = parseInt(input.value) + change;
    }

    if (newQuantity < 0) newQuantity = 0;

    try {
      this.isUpdatingFromDrawer = true;
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: itemKey,
          quantity: newQuantity,
        }),
      });

      if (response.ok) {
        const cart = await response.json();
        this.updateCartDisplay(cart);

        // Close drawer if cart is empty
        if (cart.item_count === 0) {
          setTimeout(() => this.close(), 500);
        }
      }
    } catch (error) {
      console.error("Error updating cart:", error);
    } finally {
      this.isUpdatingFromDrawer = false;
    }
  }

  async removeItem(itemKey) {
    try {
      this.isUpdatingFromDrawer = true;
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: itemKey,
          quantity: 0,
        }),
      });

      if (response.ok) {
        const cart = await response.json();
        this.updateCartDisplay(cart);

        // Close drawer if cart is empty
        if (cart.item_count === 0) {
          setTimeout(() => this.close(), 500);
        }
      }
    } catch (error) {
      console.error("Error removing item:", error);
    } finally {
      this.isUpdatingFromDrawer = false;
    }
  }

  async updateCartDisplay(cart) {
    // Update cart count in header
    const cartCountElements = document.querySelectorAll("[data-cart-count]");
    cartCountElements.forEach((el) => {
      el.textContent = cart.item_count;
      el.style.display = cart.item_count > 0 ? "inline" : "none";
    });

    // Hide/show cart button based on cart state
    const cartButtons = document.querySelectorAll(".cart-drawer-trigger");
    cartButtons.forEach((button) => {
      if (cart.item_count === 0) {
        button.style.display = "none";
      } else {
        button.style.display = "inline-flex";
      }
    });

    // Update cart drawer content
    await this.updateDrawerContent(cart);

    // Dispatch cart updated event for other components (but not for cart drawer operations)
    if (!this.isUpdatingFromDrawer && !this.isUpdatingFromTrigger) {
      document.dispatchEvent(
        new CustomEvent("cart:updated", {
          detail: { cart, source: "cart-drawer" },
        })
      );
    }
  }

  async updateDrawerContent(cart) {
    try {
      // Fetch the current cart page to get the updated cart drawer content
      const response = await fetch("/cart");
      if (!response.ok) {
        throw new Error("Failed to fetch cart page");
      }

      const html = await response.text();

      // Create a temporary DOM to parse the fetched HTML
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html;

      // Find the cart drawer content in the fetched HTML
      const fetchedCartDrawerContent = tempDiv.querySelector(".cart-drawer__content");

      if (fetchedCartDrawerContent) {
        // Replace the current cart drawer content with the fetched one
        const currentCartDrawerContent = this.drawer.querySelector(".cart-drawer__content");
        if (currentCartDrawerContent) {
          currentCartDrawerContent.innerHTML = fetchedCartDrawerContent.innerHTML;
        }
      }
    } catch (error) {
      console.error("Error updating cart drawer content:", error);
    }
  }
}

// Initialize cart drawer
document.addEventListener("DOMContentLoaded", () => {
  window.cartDrawer = new CartDrawer();
});

// Listen for cart updates from other parts of the site
document.addEventListener("cart:updated", async (event) => {
  // Don't process events that originated from the cart drawer itself
  if (event.detail.source === "cart-drawer") {
    return;
  }

  if (window.cartDrawer && event.detail.cart) {
    await window.cartDrawer.updateCartDisplay(event.detail.cart);
  }
});
